/**
 * Une inscription dont la session a disparu de la vue du lecteur.
 *
 * La jointure `session:activity_id(...)` de PostgREST rend `null` — et non une
 * erreur — quand la RLS masque la ligne jointe. Les pages qui affichent
 * l'historique d'une famille ecrivent ensuite `reg.session.stage.title` : un
 * `null` y leve, React demonte l'arbre, et la page devient blanche.
 *
 * C'est arrive le 11 septembre 2026. Une condition ajoutee a la politique de
 * `sessions` pour le catalogue a masque aux familles les sessions de leur
 * propre historique — 753 inscriptions dans un centre archive, 30 dans un
 * centre de test, 184 familles. Ecran blanc une seconde apres la connexion.
 *
 * La cause est corrigee en base : un parent lit toujours les sessions
 * auxquelles il est inscrit. Ceci est la seconde ligne de defense, posee au
 * point d'ENTREE plutot qu'a chacun des onze endroits qui affichent ces
 * champs. Une ligne dont la session manque reste visible — la famille a paye,
 * elle doit voir son inscription — mais elle le dit au lieu de tout casser.
 */

export interface SessionAffichable {
  stage: { title: string };
  start_date: string;
  end_date: string;
  center: { name: string };
  [autre: string]: unknown;
}

export const STAGE_INDISPONIBLE = 'Stage indisponible';

/**
 * Remplace une session absente par un substitut affichable.
 *
 * Les dates retombent sur celle de l'inscription : elles ne servent qu'a
 * ordonner et a formater, et une date invalide ferait lever
 * `toLocaleDateString` — soit exactement le defaut qu'on evite.
 */
/*
  `T` entre et `T` ressort, sans contrainte.

  Le premier jet contraignait `T extends { session?: unknown }` et rendait
  `T & { session: SessionAffichable }` — plus precis, mais inutilisable : les
  types generes par Supabase sont perimes dans ce depot, et la contrainte
  faisait echouer les appels sur des erreurs qui ne parlaient pas du sujet.

  Le contrat honnete est plus simple : ce sont les MEMES lignes, dont le champ
  `session` est desormais sur a lire. La signature le dit ainsi, et les
  appelants gardent le typage qu'ils avaient.
*/
function estLisible(session: unknown): boolean {
  if (!session || typeof session !== 'object') return false;
  const s = session as Record<string, unknown>;
  return Boolean(s.stage) && Boolean(s.center);
}

export function avecSessionLisible<T>(ligne: T): T {
  const brute = ligne as unknown as Record<string, unknown>;
  if (estLisible(brute?.session)) return ligne;

  const existante = (brute?.session ?? {}) as Record<string, unknown>;
  const repli = (brute?.created_at as string) ?? new Date().toISOString();
  return {
    ...brute,
    session: {
      ...existante,
      stage: { title: STAGE_INDISPONIBLE },
      center: { name: '—' },
      start_date: existante.start_date ?? repli,
      end_date: existante.end_date ?? repli,
    },
  } as unknown as T;
}

/** Le meme traitement sur une liste, avec un signalement dans la console. */
export function avecSessionsLisibles<T>(lignes: T[]): T[] {
  const manquantes = lignes.filter((l) => {
    const brute = l as unknown as Record<string, unknown>;
    return !estLisible(brute?.session);
  }).length;
  if (manquantes > 0) {
    /*
      Un avertissement et non un silence : si ce compteur remonte, une regle de
      lecture vient de se refermer sur des inscriptions existantes, et il vaut
      mieux le voir dans la console d'un navigateur que dans un signalement de
      famille trois jours plus tard.
    */
    console.warn(
      `[inscriptions] ${manquantes} inscription(s) dont la session n'est pas lisible : ` +
        'une politique de lecture masque des sessions deja reservees.',
    );
  }
  return lignes.map(avecSessionLisible);
}
